import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import localforage from 'localforage';
import { 
  User, UserX, UserCheck, AlertTriangle, RefreshCw, 
  Search, X, Heart, HeartOff, LogOut, Clock, 
  CheckCircle2, Users, Activity, Settings, Filter,
  Calendar, Eye, EyeOff, ChevronDown, MoreHorizontal, XCircle
} from 'lucide-react';

// Base URL for API
const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

// Status types with color schemes
const STATUS_TYPES = {
  CHECKED_OUT: {
    value: 'CHECKEDOUT',
    label: 'Checked Out',
    color: '#10b981',
    bgColor: '#d1fae5',
    icon: <CheckCircle2 size={20} />
  },
  NOT_CHECKED_OUT: {
    value: 'Nicht-Abgemeldet',
    label: 'Not Checked Out',
    color: '#ef4444',
    bgColor: '#fee2e2',icon: <XCircle size={20} />
  },
  LATE_CHECKOUT: {
    value: 'LATE_CHECKOUT',
    label: 'Late Check-out',
    color: '#f59e0b',
    bgColor: '#fef3c7',
    icon: <Clock size={20} />
  }
};

// Enhanced animation variants
const animations = {
  container: {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.05,
        delayChildren: 0.2
      }
    },
    exit: { opacity: 0 }
  },
  item: {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    },
    exit: { y: -20, opacity: 0 }
  },
  scale: {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    },
    exit: { scale: 0.8, opacity: 0 }
  },
  fade: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
  },
  slice: {
    hidden: { width: 0, opacity: 0 },
    visible: { width: 'auto', opacity: 1, transition: { duration: 0.3 } },
    exit: { width: 0, opacity: 0, transition: { duration: 0.2 } }
  }
};

// Helper function to format profile image URLs
const formatProfileImageUrl = (profileImagePath) => {
  if (!profileImagePath) return null;
  
  // If it's already a full URL, return it
  if (profileImagePath.startsWith('http://') || profileImagePath.startsWith('https://')) {
    return profileImagePath;
  }
  
  // If it's a relative path, prepend the base URL
  if (profileImagePath.startsWith('/')) {
    return `${BASE_URL}${profileImagePath}`;
  }
  
  // Otherwise, construct the full URL
  return `${BASE_URL}/${profileImagePath}`;
};

const StatusDashboard = ({ onClose }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [processingUser, setProcessingUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showSickUsers, setShowSickUsers] = useState(true);
  const [checkoutDeadline, setCheckoutDeadline] = useState('19:00');
  const [refreshing, setRefreshing] = useState(false);

  // Handle modal close with escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // FIXED: Use BOTH working endpoints - sick-status for data, update-status for checkout
  const fetchAllUsers = async () => {
    try {
      setRefreshing(true);
      const token = await localforage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required');
      }

      // ✅ USE SICK-STATUS ENDPOINT FOR CORRECT SICK DATA
      const response = await fetch(`${BASE_URL}/api/admin/users/sick-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      
      if (data && data.users) {
        // ✅ CORRECT PROFILE IMAGE PROCESSING
        const processedUsers = data.users.map(user => ({
          ...user,
          profileImageUrl: formatProfileImageUrl(user.profileImagePath),
          displayStatus: determineUserStatus(user),
          lastSeen: user.lastUpdateTimestamp || user.lastActivityTime,
          isProcessing: false
        }));
        
        setAllUsers(processedUsers);
        console.log(`✅ Loaded ${processedUsers.length} users successfully`);
        
        // Enhanced logging for debugging
        const statusBreakdown = processedUsers.reduce((acc, user) => {
          acc[user.status] = (acc[user.status] || 0) + 1;
          return acc;
        }, {});
        console.log('📊 Raw Status Distribution:', statusBreakdown);
        
        const displayStatusBreakdown = processedUsers.reduce((acc, user) => {
          acc[user.displayStatus] = (acc[user.displayStatus] || 0) + 1;
          return acc;
        }, {});
        console.log('📈 Display Status Distribution:', displayStatusBreakdown);
        
        // 🐛 DEBUG SICK USERS
        const sickUsersDebug = processedUsers.filter(u => u.isSick);
        console.log('🤒 SICK USERS DEBUG:', sickUsersDebug.length, sickUsersDebug.map(u => ({
          name: u.username,
          email: u.email,
          isSick: u.isSick,
          sickReason: u.sickReason,
          exemptFromDailySummary: u.exemptFromDailySummary
        })));
        
        // 🐛 DEBUG ALL USER SICK FLAGS
        const sickFlags = processedUsers.map(u => ({
          name: u.username,
          isSick: u.isSick,
          type: typeof u.isSick
        })).filter(u => u.isSick !== false && u.isSick !== null && u.isSick !== undefined);
        console.log('🔍 ALL SICK FLAGS:', sickFlags);
      }
      
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // FIXED: More robust sick detection logic
  const determineUserStatus = (user) => {
    // 🐛 DEBUG INDIVIDUAL USER SICK CHECK
    if (user.username === 'Neo Stutz' || user.username === 'Campp') {
      console.log(`🔍 SICK CHECK for ${user.username}:`, {
        isSick: user.isSick,
        type: typeof user.isSick,
        sickReason: user.sickReason,
        exemptFromDailySummary: user.exemptFromDailySummary
      });
    }
    
    // ✅ ROBUST SICK DETECTION - check multiple possible values
    const isSickValue = user.isSick;
    const isUserSick = isSickValue === true || 
                      isSickValue === 'true' || 
                      isSickValue === 1 || 
                      isSickValue === '1' ||
                      (user.sickReason && user.sickReason.length > 0); // If has sick reason, probably sick
    
    if (isUserSick) {
      console.log(`🤒 User ${user.username} is SICK (isSick: ${isSickValue}, type: ${typeof isSickValue})`);
      return 'SICK';
    }
    
    // Priority 2: Exempt users (but not sick)
    if (user.exemptFromDailySummary === true) return 'EXEMPT';

    // Parse deadline
    const [hours, minutes] = checkoutDeadline.split(':');
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const deadline = new Date(today.getTime() + (hours * 60 + parseInt(minutes)) * 60 * 1000);

    // ✅ FIXED: Use the EXACT same status values as AdminDashboard
    const currentStatus = user.status;
    
    // Check if user has checked out - EXACT matching from AdminDashboard
    if (currentStatus === 'CHECKEDOUT') {
      if (user.lastUpdateTimestamp) {
        const checkoutTime = new Date(user.lastUpdateTimestamp);
        return checkoutTime > deadline ? 'LATE_CHECKOUT' : 'CHECKED_OUT';
      }
      return 'CHECKED_OUT';
    }

    // If past deadline and not checked out
    if (now > deadline && (currentStatus === 'Nicht-Abgemeldet' || currentStatus === 'active' || !currentStatus)) {
      return 'NOT_CHECKED_OUT';
    }

    // Still working/active (before deadline)
    return 'ACTIVE';
  };

  // FIXED: Use SAME checkout logic as working AdminDashboard
  const checkoutUser = async (userId) => {
    try {
      setProcessingUser(userId);
      const token = await localforage.getItem('token');
      const user = allUsers.find(u => u.id === userId);
      
      if (!user) throw new Error('User not found');
      
      console.log(`🚪 Attempting checkout for user: ${user.username} (${user.email})`);
      
      // ✅ EXACT SAME METHOD AS WORKING ADMINDASHBOARD
      const response = await fetch(`${BASE_URL}/api/auth/update-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: user.email,
          status: 'CHECKEDOUT'  // ✅ EXACT same as AdminDashboard
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Checkout failed: ${errorText}`);
      }

      console.log('✅ Checkout successful via update-status');
      setSuccessMessage('User erfolgreich abgemeldet');
      
      // Refresh data to get updated status
      setTimeout(() => fetchAllUsers(), 500);
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('🚨 Checkout error:', err);
      setError(`Fehler beim Abmelden: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setProcessingUser(null);
    }
  };

  // FIXED: Sick user management with correct endpoints
  const markUserAsSick = async (userId, reason = 'Krank gemeldet über Status Dashboard') => {
    try {
      setProcessingUser(userId);
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/admin/users/${userId}/mark-sick`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to mark user as sick: ${errorData}`);
      }

      setSuccessMessage('User erfolgreich als krank gemeldet');
      
      // Refresh data from server
      setTimeout(() => fetchAllUsers(), 500);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error marking user as sick:', err);
      setError(`Fehler beim Krank melden: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setProcessingUser(null);
    }
  };

  const markUserAsHealthy = async (userId) => {
    try {
      setProcessingUser(userId);
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/admin/users/${userId}/mark-healthy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to mark user as healthy: ${errorData}`);
      }

      setSuccessMessage('User erfolgreich als gesund gemeldet');
      
      // Refresh data from server
      setTimeout(() => fetchAllUsers(), 500);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error marking user as healthy:', err);
      setError(`Fehler beim Gesund melden: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setProcessingUser(null);
    }
  };

  // FIXED: Correct filtering and categorization logic
  const { filteredUsers, userCategories, stats } = useMemo(() => {
    const filtered = allUsers.filter(user => {
      const matchesSearch = user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           user.email?.toLowerCase().includes(searchQuery.toLowerCase());
      
      let matchesStatus = true;
      if (statusFilter !== 'all') {
        if (statusFilter === 'healthy') {
          matchesStatus = !user.isSick;
        } else if (statusFilter === 'sick') {
          matchesStatus = user.isSick;
        } else {
          matchesStatus = user.displayStatus === statusFilter;
        }
      }
      
      return matchesSearch && matchesStatus;
    });

    // ✅ FIXED: Correct categorization with better sick detection
    const categories = {
      // Monitored users: NOT sick, NOT exempt (regardless of isActive)
      active: filtered.filter(u => {
        const isNotSick = !u.isSick && 
                         u.isSick !== 'true' && 
                         u.isSick !== 1 && 
                         u.isSick !== '1' && 
                         !u.sickReason;
        const isNotExempt = !u.exemptFromDailySummary;
        return isNotSick && isNotExempt;
      }),
      // Sick users - robust detection
      sick: filtered.filter(u => {
        const isSickValue = u.isSick;
        return isSickValue === true || 
               isSickValue === 'true' || 
               isSickValue === 1 || 
               isSickValue === '1' ||
               (u.sickReason && u.sickReason.length > 0);
      }),
      // Inactive account users
      inactive: filtered.filter(u => u.isActive === false && !u.isSick),
      // Exempt users
      exempt: filtered.filter(u => u.exemptFromDailySummary && !u.isSick)
    };

    const statistics = {
      total: allUsers.length,
      active: categories.active.length,
      checkedOut: categories.active.filter(u => u.displayStatus === 'CHECKED_OUT').length,
      notCheckedOut: categories.active.filter(u => u.displayStatus === 'NOT_CHECKED_OUT').length,
      late: categories.active.filter(u => u.displayStatus === 'LATE_CHECKOUT').length,
      stillWorking: categories.active.filter(u => u.displayStatus === 'ACTIVE').length,
      sick: categories.sick.length,
      inactive: categories.inactive.length
    };

    // Enhanced debug logging
    console.log('📊 FIXED User Categories:', {
      total: allUsers.length,
      active: categories.active.length,
      sick: categories.sick.length,
      inactive: categories.inactive.length,
      exempt: categories.exempt.length
    });
    
    console.log('📈 FIXED Checkout Statistics:', {
      checkedOut: statistics.checkedOut,
      notCheckedOut: statistics.notCheckedOut,
      late: statistics.late,
      stillWorking: statistics.stillWorking
    });

    return { filteredUsers: filtered, userCategories: categories, stats: statistics };
  }, [allUsers, searchQuery, statusFilter, checkoutDeadline]);

  // Load users on mount and refresh periodically
  useEffect(() => {
    fetchAllUsers();
    const interval = setInterval(fetchAllUsers, 30000); // Auto-refresh every 30s
    return () => clearInterval(interval);
  }, [checkoutDeadline]);

  // Helper functions
  const formatTime = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return null;
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Jetzt';
    if (diffMins < 60) return `vor ${diffMins}m`;
    if (diffMins < 1440) return `vor ${Math.floor(diffMins / 60)}h`;
    return `vor ${Math.floor(diffMins / 1440)}d`;
  };

  // Modern styling
  const styles = {
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      width: '95vw',
      maxWidth: '1600px',
      height: '90vh',
      overflow: 'hidden',
      position: 'relative',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 25px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    header: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '32px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderRadius: '20px 20px 0 0'
    },
    title: {
      fontSize: '32px',
      fontWeight: '700',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    subtitle: {
      fontSize: '16px',
      opacity: 0.9,
      marginTop: '8px',
      fontWeight: '400'
    },
    closeButton: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '12px',
      width: '44px',
      height: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: 'white'
    },
    content: {
      height: 'calc(90vh - 120px)',
      overflowY: 'auto',
      padding: '0'
    },
    controls: {
      padding: '24px 32px',
      backgroundColor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    searchContainer: {
      position: 'relative',
      flex: '1',
      minWidth: '300px'
    },
    searchInput: {
      width: '100%',
      padding: '12px 16px 12px 44px',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '16px',
      outline: 'none',
      transition: 'all 0.2s',
      backgroundColor: 'white'
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b'
    },
    controlButton: {
      padding: '12px 20px',
      border: 'none',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap'
    },
    primaryButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: 'white',
      color: '#64748b',
      border: '2px solid #e2e8f0'
    },
    timeInput: {
      padding: '12px 16px',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '14px',
      outline: 'none',
      backgroundColor: 'white'
    },
    statsContainer: {
      padding: '24px 32px',
      backgroundColor: 'white'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '20px'
    },
    statCard: {
      padding: '24px',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      backgroundColor: 'white',
      transition: 'all 0.2s',
      cursor: 'pointer'
    },
    statIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '16px'
    },
    statValue: {
      fontSize: '32px',
      fontWeight: '700',
      margin: '0 0 4px 0',
      color: '#1e293b'
    },
    statLabel: {
      fontSize: '14px',
      color: '#64748b',
      fontWeight: '500'
    },
    filtersContainer: {
      padding: '0 32px 24px',
      backgroundColor: 'white'
    },
    filterButtons: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap'
    },
    filterButton: {
      padding: '8px 16px',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      backgroundColor: 'white',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    activeFilter: {
      backgroundColor: '#3b82f6',
      borderColor: '#3b82f6',
      color: 'white'
    },
    usersContainer: {
      padding: '0 32px 32px'
    },
    sectionTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    userGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
      gap: '20px'
    },
    userCard: {
      backgroundColor: 'white',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      padding: '24px',
      transition: 'all 0.2s',
      position: 'relative',
      overflow: 'hidden'
    },
    userCardActive: {
      borderColor: '#3b82f6',
      backgroundColor: '#f0f9ff'
    },
    userCardCheckedOut: {
      borderColor: '#10b981',
      backgroundColor: '#f0fdf4'
    },
    userCardNotCheckedOut: {
      borderColor: '#ef4444',
      backgroundColor: '#fef2f2'
    },
    userCardLate: {
      borderColor: '#f59e0b',
      backgroundColor: '#fffbeb'
    },
    userCardSick: {
      borderColor: '#8b5cf6',
      backgroundColor: '#faf5ff'
    },
    userHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '20px'
    },
    profileImage: {
      width: '56px',
      height: '56px',
      borderRadius: '14px',
      marginRight: '16px',
      objectFit: 'cover',
      border: '3px solid white',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    profileImagePlaceholder: {
      width: '56px',
      height: '56px',
      borderRadius: '14px',
      marginRight: '16px',
      backgroundColor: '#f1f5f9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '3px solid white',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    userInfo: {
      flex: 1
    },
    userName: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '4px'
    },
    userEmail: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '8px'
    },
    userMeta: {
      fontSize: '12px',
      color: '#94a3b8'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: '600',
      marginBottom: '16px'
    },
    actionButtons: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '12px'
    },
    actionButton: {
      padding: '10px 16px',
      border: 'none',
      borderRadius: '10px',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px'
    },
    checkoutButton: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    sickButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    healthyButton: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '16px'
    },
    message: {
      margin: '20px 32px',
      padding: '16px 20px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      fontWeight: '500'
    },
    successMessage: {
      backgroundColor: '#dcfce7',
      color: '#166534',
      border: '1px solid #bbf7d0'
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      border: '1px solid #fecaca'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#64748b'
    }
  };

  const getCardStyle = (user) => {
    if (user.isSick) return styles.userCardSick;
    switch (user.displayStatus) {
      case 'CHECKED_OUT': return styles.userCardCheckedOut;
      case 'NOT_CHECKED_OUT': return styles.userCardNotCheckedOut;
      case 'LATE_CHECKOUT': return styles.userCardLate;
      case 'ACTIVE': return styles.userCardActive;
      default: return {};
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      CHECKED_OUT: { color: '#10b981', bg: '#dcfce7', label: 'Abgemeldet', icon: CheckCircle2 },
      NOT_CHECKED_OUT: { color: '#ef4444', bg: '#fee2e2', label: 'Nicht abgemeldet', icon: UserX },
      LATE_CHECKOUT: { color: '#f59e0b', bg: '#fef3c7', label: 'Verspätet', icon: Clock },
      ACTIVE: { color: '#3b82f6', bg: '#dbeafe', label: 'Aktiv', icon: Activity },
      SICK: { color: '#8b5cf6', bg: '#e0e7ff', label: 'Krank', icon: HeartOff }
    };
    return configs[status] || { color: '#64748b', bg: '#f1f5f9', label: 'Unbekannt', icon: User };
  };

  if (loading && allUsers.length === 0) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <motion.div 
          style={styles.modalContainer}
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div style={styles.header}>
            <div>
              <h1 style={styles.title}>
                <Activity size={32} />
                Status Dashboard
              </h1>
              <div style={styles.subtitle}>Lade alle User-Daten...</div>
            </div>
            <button style={styles.closeButton} onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          <div style={{ padding: '80px', textAlign: 'center' }}>
            <RefreshCw size={40} className="animate-spin" style={{ color: '#3b82f6', marginBottom: '20px' }} />
            <p style={{ fontSize: '18px', color: '#64748b' }}>Lade User-Daten...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <motion.div 
        style={styles.modalContainer}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>
              <Activity size={32} />
              Status Dashboard - FIXED VERSION
            </h1>
            <div style={styles.subtitle}>
              {stats.total} User gesamt • {stats.active} aktive • Deadline: {checkoutDeadline}
            </div>
          </div>
          <button 
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          >
            <X size={24} />
          </button>
        </div>

        <div style={styles.content}>
          {/* Controls */}
          <div style={styles.controls}>
            <div style={styles.searchContainer}>
              <Search size={20} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="User suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  ...styles.searchInput,
                  borderColor: searchQuery ? '#3b82f6' : '#e2e8f0'
                }}
              />
            </div>
            
            <input
              type="time"
              value={checkoutDeadline}
              onChange={(e) => setCheckoutDeadline(e.target.value)}
              style={styles.timeInput}
            />
            
            <button
              style={{
                ...styles.controlButton,
                ...(showSickUsers ? styles.primaryButton : styles.secondaryButton)
              }}
              onClick={() => setShowSickUsers(!showSickUsers)}
            >
              {showSickUsers ? <EyeOff size={16} /> : <Eye size={16} />}
              Kranke {showSickUsers ? 'verbergen' : 'anzeigen'}
            </button>
            
            <button
              style={{
                ...styles.controlButton,
                ...styles.primaryButton,
                opacity: refreshing ? 0.7 : 1
              }}
              onClick={fetchAllUsers}
              disabled={refreshing}
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              Aktualisieren
            </button>
          </div>

          {/* Messages */}
          <AnimatePresence>
            {successMessage && (
              <motion.div
                style={{ ...styles.message, ...styles.successMessage }}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <CheckCircle2 size={16} />
                {successMessage}
              </motion.div>
            )}
            
            {error && (
              <motion.div
                style={{ ...styles.message, ...styles.errorMessage }}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <AlertTriangle size={16} />
                {error}
                <button 
                  onClick={() => setError(null)}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
                >
                  <X size={16} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Statistics */}
          <div style={styles.statsContainer}>
            <div style={styles.statsGrid}>
              {[
                { key: 'checkedOut', label: 'Abgemeldet', color: '#10b981', icon: CheckCircle2 },
                { key: 'notCheckedOut', label: 'Nicht abgemeldet', color: '#ef4444', icon: UserX },
                { key: 'late', label: 'Verspätet', color: '#f59e0b', icon: Clock },
                { key: 'stillWorking', label: 'Noch aktiv', color: '#3b82f6', icon: Activity },
                { key: 'sick', label: 'Krank', color: '#8b5cf6', icon: HeartOff },
                { key: 'total', label: 'User gesamt', color: '#64748b', icon: Users }
              ].map(stat => (
                <motion.div 
                  key={stat.key}
                  style={{
                    ...styles.statCard,
                    borderColor: statusFilter === stat.key ? stat.color : '#e2e8f0'
                  }}
                  whileHover={{ y: -2, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }}
                  onClick={() => setStatusFilter(statusFilter === stat.key ? 'all' : stat.key)}
                >
                  <div style={{ ...styles.statIcon, backgroundColor: `${stat.color}15`, color: stat.color }}>
                    <stat.icon size={24} />
                  </div>
                  <div style={styles.statValue}>{stats[stat.key]}</div>
                  <div style={styles.statLabel}>{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div style={styles.filtersContainer}>
            <div style={styles.filterButtons}>
              {[
                { key: 'all', label: 'Alle', icon: Users },
                { key: 'healthy', label: 'Gesunde', icon: UserCheck },
                { key: 'CHECKED_OUT', label: 'Abgemeldet', icon: CheckCircle2 },
                { key: 'NOT_CHECKED_OUT', label: 'Nicht abgemeldet', icon: UserX },
                { key: 'LATE_CHECKOUT', label: 'Verspätet', icon: Clock },
                { key: 'ACTIVE', label: 'Aktiv', icon: Activity },
                { key: 'sick', label: 'Krank', icon: HeartOff }
              ].map(filter => (
                <button
                  key={filter.key}
                  style={{
                    ...styles.filterButton,
                    ...(statusFilter === filter.key ? styles.activeFilter : {})
                  }}
                  onClick={() => setStatusFilter(filter.key)}
                >
                  <filter.icon size={14} />
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Users */}
          <div style={styles.usersContainer}>
            {/* Active Users - Monitored Users */}
            {(statusFilter === 'all' || statusFilter === 'healthy' || 
              ['CHECKED_OUT', 'NOT_CHECKED_OUT', 'LATE_CHECKOUT', 'ACTIVE'].includes(statusFilter)) && (
              <>
                <h2 style={styles.sectionTitle}>
                  <Users size={24} />
                  Überwachte User ({userCategories.active.filter(u => 
                    statusFilter === 'all' || statusFilter === 'healthy' || u.displayStatus === statusFilter
                  ).length})
                </h2>
                
                <div style={styles.userGrid}>
                  {userCategories.active
                    .filter(u => statusFilter === 'all' || statusFilter === 'healthy' || u.displayStatus === statusFilter)
                    .map(user => {
                      const statusConfig = getStatusConfig(user.displayStatus);
                      
                      return (
                        <motion.div
                          key={user.id}
                          style={{
                            ...styles.userCard,
                            ...getCardStyle(user)
                          }}
                          whileHover={{ y: -3, boxShadow: '0 12px 30px rgba(0,0,0,0.1)' }}
                          layout
                        >
                          {processingUser === user.id && (
                            <div style={styles.loadingOverlay}>
                              <RefreshCw size={24} className="animate-spin" style={{ color: '#3b82f6' }} />
                            </div>
                          )}
                          
                          <div style={styles.userHeader}>
                            {user.profileImageUrl ? (
                              <img 
                                src={user.profileImageUrl} 
                                alt={user.username}
                                style={styles.profileImage}
                                onError={(e) => {
                                  e.target.src = '/api/placeholder/40/40';
                                }}
                              />
                            ) : (
                              <div style={styles.profileImagePlaceholder}>
                                <User size={28} color="#64748b" />
                              </div>
                            )}
                            
                            <div style={styles.userInfo}>
                              <div style={styles.userName}>{user.username}</div>
                              <div style={styles.userEmail}>{user.email}</div>
                              <div style={styles.userMeta}>
                                DB-Status: {user.status || 'null'} → Display: {user.displayStatus}
                              </div>
                              {user.lastSeen && (
                                <div style={styles.userMeta}>
                                  Zuletzt aktiv: {getRelativeTime(user.lastSeen)}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div style={{
                            ...styles.statusBadge,
                            backgroundColor: statusConfig.bg,
                            color: statusConfig.color
                          }}>
                            <statusConfig.icon size={14} />
                            {statusConfig.label}
                          </div>
                          
                          <div style={styles.actionButtons}>
                            {user.displayStatus !== 'CHECKED_OUT' && (
                              <button
                                style={{
                                  ...styles.actionButton,
                                  ...styles.checkoutButton
                                }}
                                onClick={() => checkoutUser(user.id)}
                                disabled={processingUser === user.id}
                              >
                                <LogOut size={14} />
                                Abmelden
                              </button>
                            )}
                            <button
                              style={{
                                ...styles.actionButton,
                                ...styles.sickButton
                              }}
                              onClick={() => markUserAsSick(user.id)}
                              disabled={processingUser === user.id}
                            >
                              <HeartOff size={14} />
                              Krank melden
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                </div>
              </>
            )}

            {/* Sick Users */}
            <AnimatePresence>
              {showSickUsers && userCategories.sick.length > 0 && (statusFilter === 'all' || statusFilter === 'sick') && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{ marginTop: '40px' }}
                >
                  <h2 style={styles.sectionTitle}>
                    <HeartOff size={24} />
                    Krank gemeldete User ({userCategories.sick.length})
                  </h2>
                  
                  <div style={styles.userGrid}>
                    {userCategories.sick.map(user => (
                      <motion.div
                        key={`sick-${user.id}`}
                        style={{
                          ...styles.userCard,
                          ...styles.userCardSick
                        }}
                        whileHover={{ y: -3, boxShadow: '0 12px 30px rgba(0,0,0,0.1)' }}
                        layout
                      >
                        {processingUser === user.id && (
                          <div style={styles.loadingOverlay}>
                            <RefreshCw size={24} className="animate-spin" style={{ color: '#8b5cf6' }} />
                          </div>
                        )}
                        
                        <div style={styles.userHeader}>
                          {user.profileImageUrl ? (
                            <img 
                              src={user.profileImageUrl} 
                              alt={user.username}
                              style={styles.profileImage}
                              onError={(e) => {
                                e.target.src = '/api/placeholder/40/40';
                              }}
                            />
                          ) : (
                            <div style={styles.profileImagePlaceholder}>
                              <User size={28} color="#64748b" />
                            </div>
                          )}
                          
                          <div style={styles.userInfo}>
                            <div style={styles.userName}>{user.username}</div>
                            <div style={styles.userEmail}>{user.email}</div>
                            {user.sickTimestamp && (
                              <div style={styles.userMeta}>
                                Krank seit: {formatTime(user.sickTimestamp)}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div style={{
                          ...styles.statusBadge,
                          backgroundColor: '#e0e7ff',
                          color: '#8b5cf6'
                        }}>
                          <HeartOff size={14} />
                          Krank gemeldet
                        </div>
                        
                        {user.sickReason && (
                          <div style={{
                            fontSize: '13px',
                            color: '#64748b',
                            marginBottom: '16px',
                            padding: '12px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                          }}>
                            <strong>Grund:</strong> {user.sickReason}
                          </div>
                        )}
                        
                        <div style={styles.actionButtons}>
                          <button
                            style={{
                              ...styles.actionButton,
                              ...styles.healthyButton
                            }}
                            onClick={() => markUserAsHealthy(user.id)}
                            disabled={processingUser === user.id}
                          >
                            <UserCheck size={14} />
                            Gesund melden
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Empty State */}
            {filteredUsers.length === 0 && !loading && (
              <div style={styles.emptyState}>
                <Users size={64} style={{ margin: '0 auto 20px', opacity: 0.3 }} />
                <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>Keine User gefunden</h3>
                <p>Versuche eine andere Suche oder ändere die Filter.</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default StatusDashboard;